import { BulkActionBar } from '@app/components/data-table/bulk-edit/bulk-action-bar';
import useAuth from '@app/auth/use-auth';
import { makeSelectColumn } from '@app/components/data-table/bulk-edit/select-column';
import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import { makeUpdateItemsAction } from '@app/components/item-edit/update-items-modal-body';
import { commonFilterParsers } from '@app/hooks/use-data-table-query-state';
import { buildItemScopeSearch } from '@app/purchased-goods/components/item-detail/use-item-scope';
import { toTaxonomyFilterNodes } from '@app/purchased-goods/components/taxonomy-filter-nodes';
import { useGetPurchasedGoodsTaxonomy } from '@shared/api/hooks/purchased-goods/taxonomy';
import {
  FoodActivitiesList,
  PaginatedFoodActivitiesListList,
} from '@shared/api/types';
import { toPickerPeriods } from '@app/components/multi-period-picker';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { purchasedGoodsRoutes } from '../../purchased-goods-routes';
import { FoodFilterColumn, foodFilterKeys, FoodFilterOption } from '../filters';
import { usePurchasedFoodAndDrinkTableColumns } from './purchased-food-and-drink-table-columns';
import PurchasedFoodAndDrinkTableToolbar from './purchased-food-and-drink-table-toolbar';

interface PurchasedFoodAndDrinkTableProps {
  data?: PaginatedFoodActivitiesListList;
  status?: string;
  isLoading?: boolean;
  isFetching?: boolean;
  isPreviousData?: boolean;
  isFetchedAfterMount?: boolean;
}

export default function PurchasedFoodAndDrinkTable({
  data,
  status,
  isLoading,
  isPreviousData,
  isFetchedAfterMount,
}: PurchasedFoodAndDrinkTableProps) {
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [site] = useQueryState('site', commonFilterParsers.site);
  const [supplier] = useQueryState('supplier', commonFilterParsers.supplier);
  const [period] = useQueryState('period', commonFilterParsers.period);
  const navigate = useNavigate();
  const { user } = useAuth();

  // The category filter offers the food taxonomy the organisation actually
  // bought under the active site and supplier, as the insights filter does.
  // The table's period facet is a set of periods rather than a window, so it
  // does not narrow the tree.
  const { data: taxonomy, isLoading: isTaxonomyLoading } =
    useGetPurchasedGoodsTaxonomy({
      itemType: 'food_drink',
      ...(site.length ? { site } : {}),
      ...(supplier.length ? { supplier } : {}),
    });
  const taxonomyNodes = useMemo(
    () => toTaxonomyFilterNodes(taxonomy),
    [taxonomy]
  );

  // A single selected period maps onto the detail page's range as both
  // boundaries; any other selection has no equivalent, so the detail page
  // falls back to its default window.
  const drillPeriod = period.length === 1 ? period[0] : null;
  const drillScopeSearch = buildItemScopeSearch({
    periodStart: drillPeriod,
    periodEnd: drillPeriod,
    sites: site,
  });

  const { count } = data || {};
  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const filterOptions = useServerFilterOptions(
    foodFilterKeys,
    data?.filters || {}
  );

  const useFilterOptions = useMemo(() => {
    const opts: FilterOptions<FoodFilterOption[]> = {} as FilterOptions<
      FoodFilterOption[]
    >;
    foodFilterKeys.forEach((key) => {
      opts[key] = filterOptions[`${key}Filter`];
    });
    return opts;
  }, [filterOptions]);

  const { table, context } = useServerDataTable({
    data: data?.results || [],
    columns: [
      // Selection checkboxes are staff-only (bulk actions are gated to staff).
      ...(user?.isStaff ? [makeSelectColumn<FoodActivitiesList>()] : []),
      ...usePurchasedFoodAndDrinkTableColumns({
        filters: useFilterOptions,
        timePeriods: toPickerPeriods(data?.filters?.timePeriods),
        taxonomyNodes,
        isTaxonomyLoading,
      }),
    ],
    pageCount,
    shallow: false,
    getRowId: (row) => row.uuid,
    initialState: {
      columnVisibility: {
        uuid: false,
        [FoodFilterColumn.Period]: false,
        [FoodFilterColumn.Site]: false,
        [FoodFilterColumn.ActivitiesImport]: false,
        [FoodFilterColumn.Tag]: false,
        [FoodFilterColumn.RemoteId]: false,
        [FoodFilterColumn.Supplier]: false,
        [FoodFilterColumn.Category]: false,
      },
      columnOrder: ['select', 'remoteId', 'supplier', 'food'],
    },
  });

  // Main Food and drink table: one unified "Update items" action — no
  // "Confirm" here (that's a review action, available on the Review tab).
  // Rows are keyed by activity uuid; the "Food" target re-models the food
  // activities while the other targets change-type the PurchasedGoods.
  const bulkActions = useMemo(
    () => [
      makeUpdateItemsAction({
        currentType: 'food',
        resolveUuids: (table) =>
          table
            .getFilteredSelectedRowModel()
            .rows.map(
              (row) =>
                (row.original as FoodActivitiesList).purchasedGoodInfo.uuid
            ),
        resolveFoodActivityUuids: (table) =>
          table
            .getFilteredSelectedRowModel()
            .rows.map((row) => (row.original as FoodActivitiesList).uuid),
      }),
    ],
    []
  );

  return (
    <>
      <DataTable
        table={table}
        context={context}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
        tableLoading={!data && status === 'loading'}
        className="bg-gray-00 text-left"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) =>
          navigate(
            generatePath(purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_ITEM, {
              item_uuid: row?.original.uuid,
            }) + drillScopeSearch
          )
        }
      >
        <DataTableToolbar table={table}>
          <PurchasedFoodAndDrinkTableToolbar />
        </DataTableToolbar>
      </DataTable>
      <BulkActionBar table={table} actions={bulkActions} />
    </>
  );
}
